<script lang="ts">
	import { fetchUtils } from '../../../utils/fetchUtils'
	import { addNotification } from '../../../stores'
	import { t } from '../../../../i18n/i18n'
	import type { Analytical } from '../../types/analytical'
	import { closeModal, openModal } from 'svelte-modals'
	import Modal from '../../../organisms/Modal.svelte'
	import { createEventDispatcher, onMount } from 'svelte'
	import {
		AnalyticalParentType,
		AnalyticalType,
		ButtonIcons,
		TooltipPlacements
	} from '../../../types/enums'
	import { NotificationType } from '../../../types/notification'
	import AnalyticalRowHighestValues from './AnalyticalRowHighestValues.svelte'
	import SecondaryButton, { SecondaryBtnColors } from '../../../atoms/SecondaryButton.svelte'
	import Icon from '../../../atoms/Icon.svelte'
	import iconCheckmark from '@/icons/icon_checkmark_circled.svg'
	import iconExclamationCornered from '@/icons/icon_exclamation_red_cornered.svg'
	import iconInfo from '@/icons/icon_info_blue_outline.svg'
	import Tooltip from '../../../atoms/Tooltip.svelte'

	const dispatch = createEventDispatcher()
	export let analytical: Analytical
	export let analyticalParentType: AnalyticalParentType

	const deleteTooltips: Array<Tooltip> = []

	function deleteAnalytical() {
		fetchUtils
			.post('/api/analysis/delete', {
				parentType: analyticalParentType,
				analysisId: analytical.id,
				isAnalysis: analytical.type === AnalyticalType.ANALYSIS
			})
			.then(() => {
				dispatch('updateAnalytical')
				closeModal()
				addNotification({
					message: $t('UI.map.analytic.notification.deleted'),
					type: NotificationType.SUCCESS,
					timeout: 5000,
					dismissible: true
				})
			})
			.catch(fetchUtils.catchErrorAndShowNotification())
	}

	function editAnalytical(newTab = false) {
		const path = analytical.type.toLowerCase()
		const paramKey = `${analyticalParentType.toLowerCase()}${analytical.type.toLowerCase()}`
		// eslint-disable-next-line svelte/prefer-svelte-reactivity
		const params = new URLSearchParams({ [paramKey]: String(analytical.id) })
		if (analyticalParentType !== AnalyticalParentType.CHECK) {
			params.set('origin', encodeURIComponent(window.parent.location.href)) // for back navigation; encoded so lotFormId is preserved
		}
		window.open(`/${path}?${params.toString()}`, newTab ? '_blank' : '_self')
	}

	function handlePointerDown(event: PointerEvent) {
		if (event.button === 1) {
			// Middle mouse button
			// Stop browser auto-scroll mode
			event.preventDefault()
			editAnalytical(true)
		}
	}

	function handleRedirect() {
		if (analyticalParentType === AnalyticalParentType.CHECK) {
			editAnalytical(true)
		} else {
			openModal(Modal, {
				title: $t('UI.map.editAnalysis.modal.title'),
				message: $t('UI.map.editAnalysis.modal.message'),
				onAccept: () => {
					editAnalytical()
				},
				acceptText: $t('UI.button.saveAndNext')
			})
		}
	}

	function handleDelete() {
		openModal(Modal, {
			title: $t('UI.map.deleteAnalysis.modal.title'),
			message: $t('UI.map.deleteAnalysis.modal.text', { analysis: analytical.name }),
			onDeny: () => {
				deleteAnalytical()
			},
			denyText: $t('UI.map.analysis.delete.tt')
		})
	}

	const analysisResultIcon = () => {
		if (analytical.isAiAnalysis) {
			return analytical.hasReadingErrors || analytical.hasOnlySingleExpertResult
				? iconExclamationCornered
				: iconCheckmark
		}
		return iconInfo
	}

	const analysisResultTtText = () => {
		if (analytical.isAiAnalysis) {
			return $t(
				'UI.map.analysis.tooltip.' +
					(analytical.hasReadingErrors || analytical.hasOnlySingleExpertResult
						? 'potentialErrorValues'
						: 'noErrorValues')
			)
		}
		return $t('UI.map.analysis.tooltip.manualAnalysis')
	}

	function getEvaluationResultDisplayName(evaluationResult: { name: string }): string {
		if (analyticalParentType === AnalyticalParentType.CHECK) {
			return evaluationResult.name
		}
		return evaluationResult.name === analytical.name
			? evaluationResult.name
			: analytical.name + ' - ' + evaluationResult.name
	}

	const hideDeleteTooltips = () => {
		for (let idx in deleteTooltips) {
			if (deleteTooltips[idx]) {
				deleteTooltips[idx].hide()
			}
		}
	}

	onMount(() => {
		document
			.querySelector('.analytic-table')
			?.addEventListener('scroll', hideDeleteTooltips, { passive: true })
	})
</script>

{#if analytical?.evaluationResults && Array.isArray(analytical?.evaluationResults)}
	{#each analytical?.evaluationResults as evaluationResult, i (evaluationResult?.name + i)}
		<div class="analytic-row-container">
			<div class="evaluation-result-row">
				<button
					class="analytical-edit-link"
					type="button"
					on:pointerdown={handlePointerDown}
					on:click={handleRedirect}
				>
					{getEvaluationResultDisplayName(evaluationResult)}
				</button>
				{#if analytical.type === AnalyticalType.ANALYSIS}
					<span>{analytical.description || '-'}</span>
				{/if}
			</div>
			<div class="evaluation-result-row">
				<span class="evaluation-result-text">{evaluationResult.result}</span>
				<div class="evaluation-result-results">
					<div class="evaluation-result-worst-values">
						{#if analytical.type === AnalyticalType.ANALYSIS}
							<AnalyticalRowHighestValues {evaluationResult} />
						{/if}
					</div>
				</div>
			</div>
			<div class="analytic-icon-row">
				<div class="analytic-icons">
					{#if analytical.type === AnalyticalType.ANALYSIS}
						<Tooltip
							msg={analysisResultTtText()}
							appendToObject="parent"
							zIndex={1}
							placementTtip={TooltipPlacements.LEFT}
							maxWidthTtip={384}
							delayTtip={[0, 0]}
						>
							<Icon src={analysisResultIcon()} size={analytical.isAiAnalysis ? 28 : 26} />
						</Tooltip>
					{/if}
					{#if analyticalParentType !== AnalyticalParentType.CHECK}
						<Tooltip
							bind:this={deleteTooltips[i]}
							msg={$t('UI.map.analysis.delete.tt')}
							zIndex={99999999}
							placementTtip={TooltipPlacements.RIGHT}
							maxWidthTtip={384}
							delayTtip={[0, 0]}
						>
							<SecondaryButton
								leftIcon={ButtonIcons.TRASH}
								iconSize="1.5rem"
								iconPadding="0.5rem"
								color={SecondaryBtnColors.RED}
								displayIconOnly
								on:click={handleDelete}
							/>
						</Tooltip>
					{/if}
				</div>
			</div>
		</div>
	{/each}
{/if}

<style lang="scss">
	@import '$styles/global';
	@import '$styles/autocomplete';

	.analytic-row-container {
		width: auto;
		display: grid;
		grid-template-columns: subgrid;
		grid-column: 1/-1;

		&:not(:last-of-type) {
			border-bottom: 1px solid #d9d9d9;
		}
	}

	.evaluation-result-row {
		display: flex;
		row-gap: 0.25rem;
		flex-direction: column;
		font-family: Roboto, sans-serif;
		font-size: 0.75rem;
		font-style: normal;
		font-weight: 400;
		line-height: 1.125rem; /* 150% */
		padding-top: 0.625rem;
		padding-bottom: 0.625rem;

		&:first-of-type {
			overflow: hidden;
			text-overflow: ellipsis;
		}
	}

	.evaluation-result-text {
		height: 1.25rem;
		align-content: center;
	}

	.evaluation-result-results {
		overflow: hidden;
	}

	.evaluation-result-worst-values {
		display: -webkit-box;
		line-clamp: 3;
		-webkit-line-clamp: 3;
		-webkit-box-orient: vertical;
		height: inherit;
		max-height: inherit;

		& > :global(div) {
			height: inherit;
			max-height: inherit;
		}
	}

	.analytical-edit-link {
		border: none;
		background-color: transparent;
		text-align: left;
		color: $primaryGreen;
		padding-left: 0;

		&:hover {
			text-decoration: underline;
			color: $secondaryGreen;
		}
	}

	.analytic-icon-row {
		padding-top: 0.5rem;
		padding-bottom: 0.5rem;
		align-self: start;
	}

	.analytic-icons {
		@include flex-row(0.625rem, $justify: flex-end);

		height: 100%;
	}

	.delete-icon {
		border: 2px solid $primaryRed;
		background-color: white;
		border-radius: 0.125rem;
		width: 24px;
		height: 24px;

		@include flex-col;

		&:hover {
			cursor: pointer;
			border: 2px solid $primaryDarkRed;
		}
	}

	.green-icon-edit {
		margin-right: 0;
		max-width: 24px;
		max-height: 24px;
		background-size: 20px 20px;
		background-image: url('@/icons/icon_edit_autocomplete_entry.svg');
		background-repeat: no-repeat;
		background-position: center;

		@extend .green-icon;
	}
</style>
